import { isAPIError, isNetworkError, isTimeoutError } from "./error-types";
import { logError, logWarning } from "./error-logging.service";

export interface RetryConfig {
  maxAttempts: number;
  baseDelay: number;
  maxDelay: number;
  backoffMultiplier: number;
  retryableStatusCodes: number[];
}

export interface RetryResult<T> {
  success: boolean;
  data?: T;
  error?: unknown;
  attempts: number;
  totalTime: number;
}

const DEFAULT_CONFIG: RetryConfig = {
  maxAttempts: 3,
  baseDelay: 1000,
  maxDelay: 10000,
  backoffMultiplier: 2,
  retryableStatusCodes: [408, 429, 500, 502, 503, 504],
};

export class RetryService {
  private config: RetryConfig;

  constructor(config: Partial<RetryConfig> = {}) {
    this.config = { ...DEFAULT_CONFIG, ...config };
  }

  async execute<T>(
    operation: () => Promise<T>,
    context?: { component?: string; action?: string },
  ): Promise<RetryResult<T>> {
    const startTime = Date.now();
    let lastError: unknown;
    let attempt = 0;

    while (attempt < this.config.maxAttempts) {
      attempt++;

      try {
        const data = await operation();
        return { success: true, data, attempts: attempt, totalTime: Date.now() - startTime };
      } catch (error) {
        lastError = error;

        if (!this.isRetryable(error)) break;

        if (attempt < this.config.maxAttempts) {
          const delay = this.calculateDelay(attempt);
          logWarning(
            `Tentativa ${attempt} falhou, retentando em ${delay}ms`,
            { attempt, delay },
            context,
          );
          await this.sleep(delay);
        }
      }
    }

    logError(lastError, { ...context, action: `retry_failed_after_${attempt}_attempts` });
    return {
      success: false,
      error: lastError,
      attempts: attempt,
      totalTime: Date.now() - startTime,
    };
  }

  async executeWithFallback<T>(
    operation: () => Promise<T>,
    fallback: () => Promise<T>,
    context?: { component?: string; action?: string },
  ): Promise<T> {
    const result = await this.execute(operation, context);
    if (result.success && result.data !== undefined) return result.data;

    logWarning("Usando fallback após falha", { attempts: result.attempts }, context);

    try {
      return await fallback();
    } catch (fallbackError) {
      logError(fallbackError, { ...context, action: "fallback_failed" });
      throw fallbackError;
    }
  }

  private isRetryable(error: unknown): boolean {
    if (isNetworkError(error) || isTimeoutError(error)) return true;
    if (isAPIError(error)) return this.config.retryableStatusCodes.includes(error.status);
    return false;
  }

  private calculateDelay(attempt: number): number {
    const delay = this.config.baseDelay * Math.pow(this.config.backoffMultiplier, attempt - 1);
    return Math.min(delay, this.config.maxDelay);
  }

  private sleep(ms: number): Promise<void> {
    return new Promise((resolve) => setTimeout(resolve, ms));
  }
}

export const retryService = new RetryService();

export const networkRetryConfig: Partial<RetryConfig> = {
  maxAttempts: 3,
  baseDelay: 500,
  maxDelay: 5000,
  retryableStatusCodes: [408, 429, 500, 502, 503, 504],
};

export const criticalRetryConfig: Partial<RetryConfig> = {
  maxAttempts: 5,
  baseDelay: 2000,
  maxDelay: 30000,
  retryableStatusCodes: [408, 429, 500, 502, 503, 504],
};
